import AdapterDateFnsJalali from "@date-io/date-fns-jalali";
import BadgeIcon from "@mui/icons-material/Badge";
import BusinessIcon from "@mui/icons-material/Business";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import WarningIcon from "@mui/icons-material/Warning";
import {
    Alert,
    Box,
    Button,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    Paper,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useNavigate } from "react-router-dom";

const PersonalInfoStep = ({ formData, setFormData, errors, setErrors, userProfileStatus, onAvailabilityCheck }) => {
  const navigate = useNavigate();
  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleDateChange = (field) => (date) => {
    setFormData((prev) => ({
      ...prev,
      [field]: date,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        اطلاعات شخصی
      </Typography>

      {/* هشدار برای کاربرانی که پروفایلشان تایید نشده */}
      {userProfileStatus?.isLoggedIn && !userProfileStatus?.isConfirmed && (
        <Alert 
          severity="warning" 
          icon={<WarningIcon />}
          sx={{ mb: 3 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => navigate("/account/profile")}
            >
              رفتن به پروفایل
            </Button>
          }
        >
          <Typography variant="body1" fontWeight="bold" gutterBottom>
            توجه مهم!
          </Typography>
          <Typography variant="body2">
            برای ثبت درخواست پیمانکار، ابتدا باید پروفایل خود را تکمیل و تایید کنید.
            لطفاً به صفحه پروفایل مراجعه کرده و اطلاعات خود را تکمیل کنید.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* نوع ثبت‌نام */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              bgcolor: "background.paper",
            }}
          >
            <FormControl component="fieldset">
              <FormLabel
                component="legend"
                sx={{ mb: 1.5, fontWeight: 600, color: "text.primary" }}
              >
                نوع ثبت‌نام *
              </FormLabel>
              <RadioGroup
                row
                value={formData.registrationType}
                onChange={handleChange("registrationType")}
              >
                <FormControlLabel
                  value="legal"
                  control={<Radio />}
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <BusinessIcon fontSize="small" />
                      <Typography>حقوقی (شرکت/سازمان)</Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="individual"
                  control={<Radio />}
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <PersonIcon fontSize="small" />
                      <Typography>حقیقی (شخصی)</Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.firstName}>
            <InputLabel>
              {formData.registrationType === "legal" ? "نام نماینده *" : "نام *"}
            </InputLabel>
            <OutlinedInput
              value={formData.firstName}
              onChange={handleChange("firstName")}
              startAdornment={
                <InputAdornment position="start">
                  <PersonIcon color="primary" />
                </InputAdornment>
              }
              label={formData.registrationType === "legal" ? "نام نماینده *" : "نام *"}
            />
            {errors.firstName && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                {errors.firstName}
              </Typography>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.lastName}>
            <InputLabel>
              {formData.registrationType === "legal" ? "نام خانوادگی نماینده *" : "نام خانوادگی *"}
            </InputLabel>
            <OutlinedInput
              value={formData.lastName}
              onChange={handleChange("lastName")}
              startAdornment={
                <InputAdornment position="start">
                  <PersonIcon color="primary" />
                </InputAdornment>
              }
              label={formData.registrationType === "legal" ? "نام خانوادگی نماینده *" : "نام خانوادگی *"}
            />
            {errors.lastName && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                {errors.lastName}
              </Typography>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.nationalCode}>
            <InputLabel>
              {formData.registrationType === "legal" ? "کد ملی نماینده *" : "کد ملی *"}
            </InputLabel>
            <OutlinedInput
              value={formData.nationalCode}
              onChange={handleChange("nationalCode")}
              onBlur={(event) =>
                onAvailabilityCheck?.("nationalCode", {
                  nationalCode: event.target.value,
                })
              }
              inputProps={{ maxLength: 10 }}
              startAdornment={
                <InputAdornment position="start">
                  <BadgeIcon color="primary" />
                </InputAdornment>
              }
              label={formData.registrationType === "legal" ? "کد ملی نماینده *" : "کد ملی *"}
            />
            {errors.nationalCode && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                {errors.nationalCode}
              </Typography>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.mobile}>
            <InputLabel>شماره موبایل *</InputLabel>
            <OutlinedInput
              value={formData.mobile}
              onChange={handleChange("mobile")}
              onBlur={(event) =>
                onAvailabilityCheck?.("mobile", {
                  mobile: event.target.value,
                })
              }
              inputProps={{ maxLength: 11 }}
              startAdornment={
                <InputAdornment position="start">
                  <PhoneIcon color="primary" />
                </InputAdornment>
              }
              label="شماره موبایل *"
              placeholder="09123456789"
            />
            {errors.mobile && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                {errors.mobile}
              </Typography>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.email}>
            <InputLabel>ایمیل *</InputLabel>
            <OutlinedInput
              value={formData.email}
              onChange={handleChange("email")}
              type="email"
              startAdornment={
                <InputAdornment position="start">
                  <EmailIcon color="primary" />
                </InputAdornment>
              }
              label="ایمیل *"
              placeholder="example@domain.com"
            />
            {errors.email && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                {errors.email}
              </Typography>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDateFnsJalali}>
            <DatePicker
              label={formData.registrationType === "legal" ? "تاریخ ثبت *" : "تاریخ تولد *"}
              value={formData.birthDate}
              onChange={handleDateChange("birthDate")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!errors.birthDate}
                  helperText={errors.birthDate}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            * فیلدهای اجباری
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PersonalInfoStep;
